'use client';

import React, { useState, useEffect } from 'react';
import { Play, X, Sparkles, RefreshCw, ChevronRight, Globe, Loader2, CreditCard, Home } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface NextBook {
  id: string;
  title: string;
  coverImageUrl?: string;
  primaryLanguage: string;
  totalDuration?: number;
}

interface MobileStoryEndModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRepeatStory?: () => void;
  originalBookId: string;
  currentBookId: string;
  storyTitle: string;
  currentLanguage?: string;
  showBilingualOption?: boolean;
  onAutoPlayNext: (bookId: string) => void;
  onStartGeneration?: (bookId: string, title: string) => void;
}

const AVAILABLE_LANGUAGES = [
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
];

export default function MobileStoryEndModal({
  isOpen,
  onClose,
  onRepeatStory,
  originalBookId,
  currentBookId,
  storyTitle,
  currentLanguage = 'en',
  showBilingualOption = true,
  onAutoPlayNext,
  onStartGeneration,
}: MobileStoryEndModalProps) {
  const router = useRouter();
  const [countdown, setCountdown] = useState(14);
  const [nextBook, setNextBook] = useState<NextBook | null>(null);
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('es');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [error, setError] = useState('');
  
  // State for confirmation dialogs
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'continue' | 'learn' | null>(null);
  const [userCredits, setUserCredits] = useState<number>(0);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);

  const availableLanguages = AVAILABLE_LANGUAGES.filter(
    lang => lang.code !== currentLanguage
  );

  // Fetch next book recommendation
  useEffect(() => {
    if (!isOpen) return;

    async function fetchNextBook() {
      try {
        setLoading(true);
        const response = await fetch(
          `/api/audio-book/next-recommendation?currentBookId=${currentBookId}`
        );
        if (response.ok) {
          const data = await response.json();
          setNextBook(data.nextBook);
          setReason(data.reason || 'Based on your reading history');
        }
      } catch (error) {
        console.error('[Mobile Modal] Failed to fetch next book:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchNextBook();
  }, [currentBookId, isOpen]);

  // Fetch user credits
  useEffect(() => {
    if (!isOpen) return;
    
    async function fetchUserCredits() {
      try {
        const response = await fetch('/api/user/credits');
        if (response.ok) {
          const data = await response.json();
          setUserCredits(data.credits);
        }
      } catch (error) {
        console.error('[Mobile Modal] Failed to fetch credits:', error);
      }
    }
    
    fetchUserCredits();
  }, [isOpen]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || countdown <= 0 || loading || isGenerating || showConfirmDialog || showLanguageSelector) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (nextBook) {
            // Auto-play next book
            onAutoPlayNext(nextBook.id);
            router.push(`/audio-book/${nextBook.id}?autoplay=true`);
          } else {
            // No next book, go to library
            router.push('/audio-book/library');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, countdown, loading, isGenerating, showConfirmDialog, showLanguageSelector, nextBook, onAutoPlayNext, router]);

  if (!isOpen) return null;

  // Handlers
  const handleContinueStory = async () => {
    if (showConfirmDialog) {
      // User confirmed, proceed with generation
      setShowConfirmDialog(false);
      setIsGenerating(true);
      setGenerationStatus('Creating Part 2 of your story...');
      setError('');

      try {
        const response = await fetch('/api/audio-book/continue', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ originalBookId }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to continue story');
        }

        setGenerationStatus(`✨ ${data.title} is being created!`);
        onClose();
        if (onStartGeneration) {
          onStartGeneration(data.bookId, data.title);
        }
      } catch (err) {
        console.error('[Mobile Modal] Continue Story Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to continue story');
        setIsGenerating(false);
      }
    } else {
      // Show confirmation first
      setConfirmAction('continue');
      setShowConfirmDialog(true);
    }
  };

  const handleTranslateStory = async () => {
    if (showConfirmDialog) {
      // User confirmed, proceed with generation
      setShowConfirmDialog(false);
      setIsGenerating(true);
      const langName = AVAILABLE_LANGUAGES.find(l => l.code === selectedLanguage)?.name;
      setGenerationStatus(`Creating bilingual version in ${langName}...`);
      setError('');

      try {
        const response = await fetch('/api/audio-book/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originalBookId,
            targetLanguage: selectedLanguage,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to translate story');
        }

        setGenerationStatus(`✨ Bilingual ${data.title} is being created!`);
        onClose();
        if (onStartGeneration) {
          onStartGeneration(data.bookId, data.title);
        }
      } catch (err) {
        console.error('[Mobile Modal] Translate Story Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to translate story');
        setIsGenerating(false);
      }
    } else if (!showLanguageSelector) {
      // Show language selector first
      setShowLanguageSelector(true);
    }
  };

  const handlePlayNow = () => {
    if (nextBook) {
      setCountdown(0);
      onAutoPlayNext(nextBook.id);
      router.push(`/audio-book/${nextBook.id}?autoplay=true`);
    }
  };

  const handleCancel = () => {
    setCountdown(0);
    router.push('/audio-book/library');
  };

  const handleRepeat = () => {
    setCountdown(0);
    if (onRepeatStory) {
      onRepeatStory();
    }
    onClose();
  };

  // Language Selector Modal
  const renderLanguageSelector = () => (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '1rem',
        animation: 'fadeIn 0.3s ease',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.98) 0%, rgba(118, 75, 162, 0.98) 100%)',
          borderRadius: '20px',
          padding: '1.25rem',
          maxWidth: '500px',
          width: '100%',
          color: '#fff',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        <button
          onClick={() => setShowLanguageSelector(false)}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '44px',
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <X size={22} />
        </button>

        <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', textAlign: 'center', paddingRight: '48px' }}>
          Choose Learning Language
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.75rem' }}>
          {availableLanguages.map(lang => (
            <button
              key={lang.code}
              onClick={() => {
                setSelectedLanguage(lang.code);
                setShowLanguageSelector(false);
                setConfirmAction('learn');
                setShowConfirmDialog(true);
              }}
              style={{
                padding: '1rem',
                background: 'rgba(255, 255, 255, 0.15)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.9375rem',
                fontWeight: 600,
                minHeight: '70px',
              }}
            >
              <Globe size={24} />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowLanguageSelector(false)}
          style={{
            marginTop: '1rem',
            width: '100%',
            padding: '0.875rem',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '12px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '0.9375rem',
            fontWeight: 600,
            transition: 'all 0.2s ease',
            minHeight: '54px',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // Confirmation Dialog
  const renderConfirmDialog = () => {
    const cost = confirmAction === 'continue' ? 3 : 3;
    const actionName = confirmAction === 'continue' 
      ? 'Continue Story' 
      : `Learn in ${AVAILABLE_LANGUAGES.find(l => l.code === selectedLanguage)?.name}`;
    const hasEnoughCredits = userCredits >= cost;
    
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '1rem',
          animation: 'fadeIn 0.3s ease',
        }}
      >
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.98) 0%, rgba(118, 75, 162, 0.98) 100%)',
            borderRadius: '20px',
            padding: '1.25rem',
            maxWidth: '450px',
            width: '100%',
            color: '#fff',
            position: 'relative',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          }}
        >
          <button
            onClick={() => setShowConfirmDialog(false)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <X size={22} />
          </button>

          <div style={{ textAlign: 'center', marginBottom: '1rem', paddingRight: '48px' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 0.75rem',
              }}
            >
              <CreditCard size={32} />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.25rem' }}>
              {actionName}
            </h3>
          </div>

          <div
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              padding: '1rem',
              marginBottom: '1rem',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '1rem' }}>
              <span style={{ opacity: 0.9 }}>Cost:</span>
              <span style={{ fontWeight: 700 }}>{cost} credits</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem', fontSize: '1rem' }}>
              <span style={{ opacity: 0.9 }}>Your credits:</span>
              <span style={{ fontWeight: 700 }}>{userCredits} credits</span>
            </div>
            <div
              style={{
                borderTop: '2px solid rgba(255, 255, 255, 0.2)',
                paddingTop: '0.75rem',
                marginTop: '0.75rem',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '1rem',
              }}
            >
              <span style={{ opacity: 0.9 }}>After:</span>
              <span style={{ fontWeight: 700 }}>
                {userCredits - cost} credits
              </span>
            </div>
          </div>

          {!hasEnoughCredits && (
            <div
              style={{
                background: 'rgba(255, 107, 107, 0.2)',
                border: '2px solid rgba(255, 107, 107, 0.5)',
                borderRadius: '12px',
                padding: '0.875rem',
                marginBottom: '1rem',
                textAlign: 'center',
              }}
            >
              <p style={{ color: '#ffcccb', fontWeight: 600, marginBottom: '0.375rem', fontSize: '1rem' }}>
                Insufficient Credits
              </p>
              <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                You need {cost - userCredits} more credit{cost - userCredits !== 1 ? 's' : ''} to {actionName.toLowerCase()}.
              </p>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowConfirmDialog(false)}
              style={{
                flex: 1,
                padding: '0.875rem',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                transition: 'all 0.2s ease',
                minHeight: '54px',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (confirmAction === 'continue') {
                  handleContinueStory();
                } else {
                  handleTranslateStory();
                }
              }}
              disabled={!hasEnoughCredits}
              style={{
                flex: 1,
                padding: '0.875rem',
                background: hasEnoughCredits ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.3)',
                border: 'none',
                borderRadius: '12px',
                color: hasEnoughCredits ? '#667eea' : 'rgba(255, 255, 255, 0.5)',
                cursor: hasEnoughCredits ? 'pointer' : 'not-allowed',
                fontSize: '1rem',
                fontWeight: 700,
                transition: 'all 0.2s ease',
                minHeight: '54px',
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show language selector if active
  if (showLanguageSelector) return renderLanguageSelector();
  
  // Show confirmation dialog if active
  if (showConfirmDialog) return renderConfirmDialog();

  // Loading state
  if (loading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '1rem',
        }}
      >
        <div style={{ textAlign: 'center', color: '#fff' }}>
          <Sparkles size={56} className="animate-spin" style={{ margin: '0 auto 1rem' }} />
          <p style={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Finding your next story...
          </p>
        </div>
      </div>
    );
  }

  // Generating state
  if (isGenerating) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '1rem',
        }}
      >
        <div style={{ textAlign: 'center', color: '#fff', maxWidth: '400px' }}>
          <Loader2 size={56} className="animate-spin" style={{ margin: '0 auto 1rem' }} />
          <p style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            {generationStatus}
          </p>
          {error && (
            <p style={{ fontSize: '1rem', color: '#ff6b6b', marginTop: '1rem' }}>
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Main modal
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '1rem',
        animation: 'fadeIn 0.3s ease',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.98) 0%, rgba(118, 75, 162, 0.98) 100%)',
          borderRadius: '20px',
          padding: '1.25rem',
          maxWidth: '600px',
          width: '100%',
          color: '#fff',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          marginTop: 'auto',
          marginBottom: 'auto',
        }}
      >
        {/* Close/Home button */}
        <button
          onClick={handleCancel}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            zIndex: 10,
          }}
        >
          <Home size={24} />
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.75rem',
            }}
          >
            <Sparkles size={32} />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>
            Story Complete!
          </h2>
          <p style={{ fontSize: '0.9375rem', opacity: 0.9 }}>
            What would you like to do next?
          </p>
        </div>

        {/* Countdown Timer */}
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <div
            style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 0.5rem',
              fontSize: '1.75rem',
              fontWeight: 700,
              border: '3px solid rgba(255, 255, 255, 0.4)',
              animation: countdown <= 3 ? 'pulse 0.5s ease infinite' : 'none',
            }}
          >
            {countdown}
          </div>
          <p style={{ fontSize: '0.875rem', opacity: 0.85 }}>
            {nextBook 
              ? `Auto-playing next story in ${countdown} second${countdown !== 1 ? 's' : ''}...`
              : `Returning to library in ${countdown} second${countdown !== 1 ? 's' : ''}...`
            }
          </p>
        </div>

        {/* Action Buttons Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: showBilingualOption ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '1rem' }}>
          {/* Repeat Story */}
          <button
            onClick={handleRepeat}
            style={{
              padding: '0.75rem 0.5rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.35rem',
              minHeight: '85px',
            }}
          >
            <RefreshCw size={24} />
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, textAlign: 'center', lineHeight: '1.2' }}>Repeat Story</span>
          </button>

          {/* Continue Story */}
          <button
            onClick={handleContinueStory}
            style={{
              padding: '0.75rem 0.5rem',
              background: 'rgba(255, 255, 255, 0.15)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '12px',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.35rem',
              minHeight: '85px',
            }}
          >
            <ChevronRight size={24} />
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, textAlign: 'center', lineHeight: '1.2' }}>Continue Story</span>
            <span style={{ fontSize: '0.6875rem', opacity: 0.75 }}>3 credits</span>
          </button>

          {/* Learn in New Language */}
          {showBilingualOption && (
            <button
              onClick={handleTranslateStory}
              style={{
                padding: '0.75rem 0.5rem',
                background: 'rgba(255, 255, 255, 0.15)',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.35rem',
                minHeight: '85px',
              }}
            >
              <Globe size={24} />
              <span style={{ fontSize: '0.8125rem', fontWeight: 600, textAlign: 'center', lineHeight: '1.2' }}>Learn Language</span>
              <span style={{ fontSize: '0.6875rem', opacity: 0.75 }}>3 credits</span>
            </button>
          )}
        </div>

        {/* Divider */}
        {nextBook && <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.2)', margin: '1rem 0' }} />}

        {/* Next Story Section */}
        {nextBook ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                Up Next
              </h3>
              <p style={{ fontSize: '0.875rem', opacity: 0.9 }}>{reason}</p>
            </div>

            {/* Next book preview */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                background: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '12px',
                padding: '0.75rem',
                marginBottom: '0.75rem',
              }}
            >
              {nextBook.coverImageUrl && (
                <img
                  src={nextBook.coverImageUrl}
                  alt={nextBook.title}
                  style={{
                    width: '140px',
                    height: '140px',
                    objectFit: 'cover',
                    borderRadius: '10px',
                    boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
                    flexShrink: 0,
                  }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', lineHeight: '1.3' }}>
                  {nextBook.title}
                </h4>
                {nextBook.totalDuration && (
                  <p style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                    {Math.ceil(nextBook.totalDuration / 60)} min
                  </p>
                )}
              </div>
            </div>

            {/* Play Now Button */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={handlePlayNow}
                style={{
                  padding: '1rem 2rem',
                  background: '#fff',
                  color: '#667eea',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 6px 16px rgba(255, 255, 255, 0.2)',
                  minHeight: '56px',
                }}
              >
                <Play size={20} fill="currentColor" />
                Play Now
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ fontSize: '0.9375rem', opacity: 0.9 }}>
              No more recommendations available.
            </p>
          </div>
        )}

        <style jsx global>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}</style>
      </div>
    </div>
  );
}

